import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';

import usePhotoManagerSubscriptionsCheckout from '../../usePhotoManagerSubscriptionCheckout';
import PhotoManagerActiveSubscription from './PhotoManagerActiveSubscription';
import { formatPriceForDisplay } from '../../../../../../../util/format-money';
import Button from '../../../../../../../components/global/Button';
import {
  isEligibleForPhotoManagerFreeTrial,
  PHOTO_MANAGER_SUBSCRIPTION_BILLING_PERIOD,
  PHOTO_MANAGER_SUBSCRIPTION_FEATURES,
} from './helpers';
import NativeActivityIndicator from '../../../../../../../components/global/NativeActivityIndicator';
import PhotoManagerCreditCardCaptureModal from '../../../../Album/AlbumCreateWizard/AlbumCreateDeliveryPreferenceStep/PhotoManagerCreditCardCaptureModal';
import PriceDisplay from '../../../../../../../components/global/PriceDisplay';
import PhotosIcon from '../../../../../../../components/icons/PhotosIcon';
import PillButtonGroup from '../../../../../../../components/global/PillButtonGroup';
import { getDiscountPrice } from '../../../../../../../util/promo-codes';
import { ACCOUNT_REFERRAL_TYPE } from '../../../../../../../constants/account-referrals';

const propTypes = {
  className: PropTypes.string,
  onCheckoutSuccess: PropTypes.func,
  displayActiveSubscription: PropTypes.bool,
};

const PhotoManagerSubscriptionCheckout = ({
  className,
  onCheckoutSuccess,
  displayActiveSubscription,
}) => {
  const classes = classNames({
    'photo-manager-subscription-checkout': true,
    [className]: !!className,
  });

  const {
    photoManagerSubscriptionItems,
    account,
    onCheckout,
    checkingOut,
    setPromoCode,
    accountPromoCode,
    setAccountPromoCode,
  } = usePhotoManagerSubscriptionsCheckout({ onCheckoutSuccess });

  const [activeSubscription] = account?.photo_manager_subscriptions?.records;

  const availablePhotoManagerSubscriptionItems = [
    ...(photoManagerSubscriptionItems ?? []),
  ].sort((a, b) => {
    // Monthly first
    if (a.days < b.days) {
      return -1;
    }

    if (a.days > b.days) {
      return 1;
    }

    return 0;
  });

  const [
    selectedPhotoManagerSubscriptionItem,
    setSelectedPhotoManagerSubscriptionItem,
  ] = useState(null);
  const [planSelected, setPlanSelected] = useState(false);

  useEffect(() => {
    if (
      !selectedPhotoManagerSubscriptionItem &&
      (availablePhotoManagerSubscriptionItems ?? []).length > 0
    ) {
      setSelectedPhotoManagerSubscriptionItem(
        availablePhotoManagerSubscriptionItems.find(sub =>
          activeSubscription
            ? sub.billing_period === activeSubscription.billing_period
            : sub.billing_period ===
              PHOTO_MANAGER_SUBSCRIPTION_BILLING_PERIOD.MONTHLY,
        ),
      );
    }
  }, [availablePhotoManagerSubscriptionItems, activeSubscription]);

  if (!account || !photoManagerSubscriptionItems) {
    return null;
  }

  const activeSubscriptionIsYearly =
    activeSubscription?.billing_period ===
    PHOTO_MANAGER_SUBSCRIPTION_BILLING_PERIOD.YEARLY;
  const activeSubscriptionIsCancelled = !!activeSubscription?.cancelled_at;
  const onFreeTrial = activeSubscription?.trial_days > 0;

  const selectedSubscriptionIsMonthly =
    selectedPhotoManagerSubscriptionItem?.billing_period ===
    PHOTO_MANAGER_SUBSCRIPTION_BILLING_PERIOD.MONTHLY;

  return (
    <div className={classes}>
      {activeSubscription && displayActiveSubscription && (
        <PhotoManagerActiveSubscription
          account={account}
          subscription={activeSubscription}
          className="mb-3"
        />
      )}

      {
        // Show only relevant subs depending on eligibility
        (!activeSubscriptionIsYearly || onFreeTrial) && (
          <Fragment>
            {availablePhotoManagerSubscriptionItems?.length > 0 && (
              <PillButtonGroup className="photo-manager-subscription-pill-group d-flex justify-content-center mb-2">
                {availablePhotoManagerSubscriptionItems.map(
                  photoManagerSubscriptionItem => {
                    const monthly =
                      photoManagerSubscriptionItem.billing_period ===
                      PHOTO_MANAGER_SUBSCRIPTION_BILLING_PERIOD.MONTHLY;

                    if (
                      onFreeTrial &&
                      activeSubscriptionIsYearly &&
                      monthly &&
                      !activeSubscriptionIsCancelled
                    ) {
                      return null;
                    }

                    return (
                      <button
                        key={photoManagerSubscriptionItem.id}
                        disabled={checkingOut}
                        className={classNames({
                          'photo-manager-subscription-pill': true,
                          active:
                            photoManagerSubscriptionItem ===
                            selectedPhotoManagerSubscriptionItem,
                        })}
                        onClick={() =>
                          setSelectedPhotoManagerSubscriptionItem(
                            photoManagerSubscriptionItem,
                          )
                        }
                      >
                        {monthly ? 'Monthly' : 'Annual'}
                        {!monthly && (
                          <span className="photo-manager-subscription-pill-tag">
                            Save 17%!
                          </span>
                        )}
                      </button>
                    );
                  },
                )}
              </PillButtonGroup>
            )}
            <div className="photo-manager-subscription-selection-wrap">
              <div className="d-flex align-items-center mb-2">
                <div className="photo-manager-subscription-title-icon">
                  <PhotosIcon color="white" size={14} />
                </div>{' '}
                <h4 className="mb-0">Photo Manager</h4>
              </div>

              <ul className="photo-manager-subscription-features">
                {PHOTO_MANAGER_SUBSCRIPTION_FEATURES.map(feature => (
                  <li key={feature}>{feature}</li>
                ))}
              </ul>

              {selectedPhotoManagerSubscriptionItem && (
                <div className="ml-auto photo-manager-subscription-price-label">
                  $
                  {formatPriceForDisplay(
                    selectedPhotoManagerSubscriptionItem.price.value,
                    true,
                  )}
                </div>
              )}

              {checkingOut && (
                <div className="photo-manager-subscriptions-activity-indicator">
                  <NativeActivityIndicator />
                </div>
              )}

              <PhotoManagerCreditCardCaptureModal
                forceOpen={planSelected}
                onClose={() => {
                  setPlanSelected(false);
                  setPromoCode(null);
                  setAccountPromoCode(null);
                }}
                title={
                  <h3>
                    {isEligibleForPhotoManagerFreeTrial(account)
                      ? `Photo Manager + Free Trial`
                      : `Photo Manager Checkout`}
                  </h3>
                }
                description={
                  <p>
                    {isEligibleForPhotoManagerFreeTrial(account)
                      ? `$${formatPriceForDisplay(
                          selectedPhotoManagerSubscriptionItem?.price.value,
                          true,
                        )} will be charged on ${moment()
                          .add(accountPromoCode?.trial_days || 30, 'days')
                          .format('MM/DD')}`
                      : `${
                          selectedSubscriptionIsMonthly
                            ? 'Monthly Plan'
                            : 'Yearly Plan'
                        } - Your
                  subscription will start immediately.`}
                  </p>
                }
                buttonLabel={(() => {
                  if (accountPromoCode?.trial_days > 0) {
                    return 'Subscribe';
                  }

                  if (accountPromoCode) {
                    return (
                      <>
                        Confirm &amp; Pay -{' '}
                        <PriceDisplay
                          oldPrice={
                            selectedPhotoManagerSubscriptionItem?.price?.value
                          }
                          price={
                            accountPromoCode.type ===
                            ACCOUNT_REFERRAL_TYPE.PHOTO_MANAGER_YEARLY_50_PC_OFF_ONCE
                              ? selectedPhotoManagerSubscriptionItem?.price
                                  ?.value / 2
                              : getDiscountPrice(
                                  selectedPhotoManagerSubscriptionItem?.price
                                    ?.value,
                                  accountPromoCode,
                                )
                          }
                          showDiscount
                          animatedStrikethrough
                          trimDecimalZeros
                        />
                      </>
                    );
                  }

                  if (isEligibleForPhotoManagerFreeTrial(account)) {
                    return 'Subscribe';
                  }

                  return `Confirm & Pay - $${formatPriceForDisplay(
                    selectedPhotoManagerSubscriptionItem?.price?.value,
                    true,
                  )}`;
                })()}
                supportsPromoCode
                billingPeriod={
                  selectedPhotoManagerSubscriptionItem?.billing_period
                }
                onPromoCodeChange={code => setPromoCode(code)}
                onPromoCodeVerified={accountCode => {
                  setAccountPromoCode(accountCode);
                }}
                onSuccess={stripeToken => {
                  onCheckout({
                    photoManagerSubscriptionItemId:
                      selectedPhotoManagerSubscriptionItem?.id,
                    stripeToken,
                  });
                  setSelectedPhotoManagerSubscriptionItem(null);
                }}
              />
            </div>
            <div className="d-flex justify-content-center my-4">
              <Button
                btnSize="lg"
                onClick={() => {
                  if (account.default_payment_method) {
                    onCheckout({
                      photoManagerSubscriptionItemId:
                        selectedPhotoManagerSubscriptionItem?.id,
                      stripeToken: null,
                    });
                  } else {
                    setPlanSelected(true);
                  }
                }}
                disabled={!selectedPhotoManagerSubscriptionItem || checkingOut}
              >
                {checkingOut ? 'Checking out...' : 'Upgrade Subscription Now'}
              </Button>
            </div>
          </Fragment>
        )
      }
    </div>
  );
};

PhotoManagerSubscriptionCheckout.propTypes = propTypes;

export default PhotoManagerSubscriptionCheckout;
